import { useMemo } from 'react';
import useApiClient from './_use-api-client';
import {
  useApiExternalGet,
  useApiExternalGetBulk,
  useApiGet,
  useApiGetBulk,
  useApiPost,
  screenerApiPost,
  screenerApiGet,
  screenerExternalApiGet
} from './_use-api';
import { PROFILE_API_TYPES } from './_constants';
import { FUND_TYPES } from '../../stores/profile/get-fund-type';
import getFundType from '../../stores/profile/get-fund-type';
import globals from '../../utils/globals';
import { tryParseJson } from '../../utils/sharedHelpers';
import _get from 'lodash/get';
import screenerApiClient from './_screener-api-client';

export { useApiClient, PROFILE_API_TYPES, screenerApiClient };

export const useChartHistoricalReturns = ({ params, ...rest }) =>
  useApiPost({
    ...rest,
    path: '/chart/historical-returns',
    params,
    dependencies: [params.period, params.width, params.xid, rest.shouldLoad]
  });

export const useChartHypotheticalGrowth = ({ params, ...rest }) =>
  useApiPost({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8828 consolidating Hypo10k chart API
    path: `/chart/hypothetical-growth-of-10k`,
    params,
    dependencies: [params.timeFrame, params.width, params.xid, rest.shouldLoad]
  });

export const useChartHypotheticalGrowthCompare = ({ params, ...rest }) =>
  useApiPost({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8829 consolidating Hypo10k compare chart API
    path: `/chart/hypothetical-growth-of-10k-compare`,
    params,
    dependencies: [params.timeFrame, params.width, params.xid]
  });

export const useChartMoneyMarketPriceLine = ({ params, ...rest }) =>
  useApiPost({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8830 consolidating Money market price line chart API
    path: `/chart/money-market-price-line`,
    params,
    dependencies: [params.timeFrame, params.width, params.xid, params.dataType]
  });

export const useChartPerformanceReturns = ({ params, ...rest }) =>
  useApiPost({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8831 consolidating performance returns chart API
    path: '/chart/performance-returns',
    params,
    dependencies: [params.period, params.width, params.xid]
  });

export const useChartPrice = ({ params, ...rest }) =>
  useApiPost({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8832 consolidating Price chart API
    path: `/chart/price-chart`,
    params,
    dependencies: [
      params.numberOfDays,
      params.height,
      params.width,
      params.xid,
      params.lineColor,
      params.fillColor,
      params.showAsTable,
      rest.shouldLoad
    ]
  });

export const useProfileAssetAllocation = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8795 Consolidating Asset Allocation API
    path: `/profile/asset-allocation/${xid}`
  });

export const useProfileAuxiliary = ({ apiType, symbol, xid = null, mpTicker = null, ...rest }) => {
  const xidUrl = mpTicker ? `${xid}?mpTicker=${mpTicker}` : xid;
  return useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8796, TIAA-8797 Consolidating Auxiliary API
    path: xid
      ? `/profile/auxiliary-by-xid/${xidUrl}`
      : `/profile/auxiliary-by-symbol/${encodeURIComponent(symbol)}`
  });
};

export const useProfileContractFees = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/profile/contract-fees/${xid}`
  });

export const useProfileDisclosures = ({ channel, xid, forPrint, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/disclosures/${xid}?channel=${channel}&forPrint=${forPrint}`
  });

export const useExpenseExample = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/quote/expense-example/${xid}`
  });

export const useProfileFeesExpenses = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/fees-expense/${xid}`
  });

export const useProfileFootnotes = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/footnotes/${xid}`,
    dependencies: [rest.shouldLoad]
  });

export const useProfileFundNotification = ({ symbol, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/fund-notification/${symbol}`
  });

export const useProfileGeographicDistribution = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/geographic-distribution/${xid}`
  });

export const useProfileGlossaryTerms = ({ ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: '/profile/glossary-terms'
  });

export const useProfileHypotheticalGrowth = ({ timeFrame, xid, isCITFund, ...rest }) => {
  const path = !isCITFund ? `/profile/hypothetical-growth-of-10k/${xid}?timeframe=${timeFrame}` : `/profile/hypothetical-growth-of-10k-compare?xids=${xid}&timeframe=${timeFrame}`;

  return useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path
  });
};

export const useProfileIncomePayoutRates = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/profile/income-payout-rates/${xid}`
  });

export const useProfileIncomeWithdrawalOptions = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/profile/income-withdrawal-options/${xid}`
  });

export const useProfileInterestCreditingRates = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/profile/interest-crediting-rates/${xid}`
  });

export const useProfileInvestmentByType = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/profile/investment-by-type/${xid}`
  });

export const useProfileInvestmentFacts = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/investment-facts/${xid}`
  });

export const useProfileInvestmentHoldings = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8810 consolidating investment-holding API
    path: `/profile/investment-holding/${xid}`
  });

export const useProfileInvestmentStrategy = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8811 Consolidating profile/investment-strategy
    path: `/profile/investment-strategy/${xid}`
  });

export const useProfileMarketCapitalization = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8813 Consolidating profile/market-capitalization API
    path: `/profile/market-capitalization/${xid}`
  });

export const useAllHoldings = ({ xid, startRow, numRows, sortDir, sortBy, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/allHoldings/all-holdings/${xid}?startRow=${startRow}&numRows=${numRows}&sortDir=${sortDir}&sortBy=${sortBy}`,
    dependencies: [sortDir, sortBy]
  });

export const useProfileMaturityAllocation = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8812 Consolidating profile/maturity-allocation API
    path: `/profile/maturity-allocation/${xid}`
  });

export const useProfileMoneyMarketHoldings = ({ xid, params, ...rest }) =>
  useApiGet({
    ...rest,
    params,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8816 Consolidating profile/money-market-holdings API
    path: `/profile/money-market-holdings/${xid}`
  });

export const useProfileMorningstarFactsRatings = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8817 Consolidating profile/morningstar-facts-ratings API
    path: `/profile/morningstar-facts-ratings/${xid}`
  });

export const useProfilePerformanceReturns = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8818 Consolidating profile/performance-returns API
    path: `/profile/performance-returns/${xid}`
  });

export const useProfilePlanHoldingsBulk = ({ params: paramsArray, shouldLoad = true, ...rest }) => {
  const path = '/profile/plan-holdings';
  const configs = useMemo(() => {
    return paramsArray ? paramsArray.map(params => ({ path, params })) : [];
  }, [paramsArray]);
  return useApiGetBulk({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8819 consolidating plan holdings API
    configs,
    shouldLoad,
    debugLabel: path,
    dependencies: paramsArray.map(
      ({ planNumber, subPlan }) => `${planNumber || ''}|${subPlan || ''}`
    )
  });
};

export const useProfilePortfolioManagers = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8820 consolidating portfolio managers API
    path: `/profile/portfolio-managers/${xid}`
  });

export const useProfilePriceChart = ({ xid, numberOfDays, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/price-chart/${xid}?numberOfDays=${numberOfDays}`
  });

export const useProfilePriceChartDailyData = ({ xid, numberOfDays, isDaily, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/price-chart/${xid}?numberOfDays=${numberOfDays}&isDaily=${isDaily}`
  });

export const useProfileRiskCategories = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8822 consolidating Risk categories API
    path: `/profile/risk-categories/${xid}`
  });

export const useProfileRealEstateProperties = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/profile/real-estate-properties/${xid}`
  });

export const useProfileRiskMeasures = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/profile/risk-measures/${xid}`
  });

export const useProfileSectorDiversification = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8824 consolidating sector diversification API
    path: `/profile/sector-diversification/${xid}`
  });

export const useProfileShareClassDocuments = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8825 consolidating Share class documents API
    path: `/profile/share-class-documents/${xid}`
  });

export const useProfileTsvInterestRate = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/profile/tsv-interest-rate/${xid}`
  });
export const useProfileUnderlyingInvestments = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    path: `/profile/model-underlying-investments/${xid}`
  });

export const usePortfolioHoldings = ({ channel = '', shouldLoad = true }) => {
  const isChannelMyTiaa = channel.toLowerCase() === 'mytiaa';
  const isChannelIFA = channel.toLowerCase() === 'ifa';
  let formattedUrl = globals.participantHoldingsApiUrl.myTiaa;
  if (isChannelIFA) {
    formattedUrl = globals.participantHoldingsApiUrl.ifa;
    shouldLoad = false;
  }
  const {
    isLoading,
    isLoaded,
    data: response,
    error
  } = useApiExternalGet({
    config: {
      shouldLoad: (isChannelMyTiaa) && shouldLoad,
      url: formattedUrl
    }
  });
  //See test file for example response, to test logic manually, replace 'useApiExternalGet' with makeMock(mockPortfolioHoldings)
  const resultRaw =
    response && response.data
      ? response.data.slice(
        response.data.indexOf('_information(') + 13,
        response.data.lastIndexOf(')')
      )
      : {};
  const resultParsed = tryParseJson(resultRaw) || {};

  const data = _get(resultParsed, 'apps[0].data.plans', []);

  return {
    isLoading,
    isLoaded,
    data,
    error
  };
};

/*
const mockNoticeUrlReturn = [
  {
    data: {
      data: 'myCallbackName([{"title":"Plan and Investment Notice","url":"https://www.tiaa.org/public/pdf/obiee/102373_Plan_Investment_Notice.pdf"}])'
    }
  },
  {
    data: {
      data: 'myCallbackName([{"title":"Plan and Investment Notice","url":"https://www.tiaa.org/public/pdf/obiee/102374_Plan_Investment_Notice.pdf"}])'
    }
  }
];
*/

export const usePlanNoticeUrl = ({ planNumbers, channel = '', shouldLoad = true }) => {
  //Callback Query param required to access response
  const callbackQueryParam = 'callback=myCallbackName';
  const isChannelMyTiaa = channel.toLowerCase() === 'mytiaa';
  const isChannelIFA = channel.toLowerCase() === 'ifa';

  let urlPath = isChannelMyTiaa ? globals?.planNoticeGetUrl?.myTiaa : globals?.planNoticeGetUrl?.default;
  if (isChannelIFA) {
    urlPath = globals?.planNoticeGetUrl?.ifa;
  }
  const configs = planNumbers
    ? planNumbers.map(planNumber => ({
      url: `${urlPath}${planNumber}?${callbackQueryParam}`,
      shouldLoad
    }))
    : [];

  const { isLoading, isLoaded, data: response, error } =
    // Use the below for testing locally
    // makeMock(mockNoticeUrlReturn);
    useApiExternalGetBulk({
      configs,
      shouldLoad
    });
  const data = response && response[0]?.data
    ? response.map(singleResponse => {
      const returnString = singleResponse.data.data;
      const url = returnString.slice(
        returnString.indexOf('https'),
        returnString.lastIndexOf('pdf') + 3
      );
      return url;
    })
    : [];

  return {
    isLoading,
    isLoaded,
    data,
    error
  };
};

export const useQuote = ({ xid, ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop, // TIAA-8826 consolidating Quote API
    path: `/quote/${xid}`
  });

export const useXrefExact = ({ symbol, xid, ...rest }) => {
  return useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: xid ? `/xref/exact/xid/${xid}` : `/xref/exact/symbol/${encodeURIComponent(symbol)}`
  });
};

export const screenerAPI = ({ params, screenId, ...rest }) =>
  screenerApiPost({
    ...rest,
    apiType: PROFILE_API_TYPES.platform,
    path: `${screenId}`,
    params,
    dependencies: [params.screenInputs]
  });

export const useTagDefinitions = ({ ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: '/profile/tag-definitions'
  });

export const useScreenerDistributionDates = ({ ...rest }) =>
  useApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/screener/distribution-dates`
  });

export const getScreenerPlanHoldings = ({ params, ...rest }) => {
  const planHoldings = [];
  for (const param of params) {
    const subPlanParams = param.subPlan ? `&subPlan=${encodeURIComponent(param.subPlan)}` : '';
    planHoldings.push(
      screenerApiGet({
        ...rest,
        apiType: PROFILE_API_TYPES.prop, // TIAA-8819 consolidating plan holdings API
        path: `/profile/plan-holdings?planNumber=${encodeURIComponent(param.planNumber)}&includeDisclosures=${encodeURIComponent(param.includeDisclosures)}${subPlanParams}`,
        dependencies: `${param.planNumber || ''}|${param.subPlan || ''}`
      })
    );
  }
  return planHoldings;
};

export const getParticipantHoldings = ({ channel }) => {
  const isChannelIFA = channel.toLowerCase() === 'ifa';
  let formattedUrl = globals.participantHoldingsApiUrl.myTiaa;
  if (isChannelIFA) {
    formattedUrl = globals.participantHoldingsApiUrl.ifa;
  }
  return screenerExternalApiGet(formattedUrl);
};

export const getFootNotesLabel = ({ ...rest }) => {
  return screenerApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/screener/footnotes-label`
  });
};

export const getDocumentsForFunds = ({ xid, ...rest }) => {
  return screenerApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/share-class-documents/${xid}`
  });
};

export const getProfileFootnotes = ({ xid, ...rest }) => {
  return screenerApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/footnotes/${xid}`
  });
};

export const getProfileFootnotesBulk = ({ xidArray, ...rest }) => {
  return screenerApiPost({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/screener/footnotes-bulk`,
    params: { xids: xidArray }
  });
};

export const getHypotheticalGrowth = ({ xid, timeFrame, isCIT, ...rest }) => {
  if (isCIT) {
    return screenerApiGet({
      ...rest,
      apiType: PROFILE_API_TYPES.prop,
      path: `/profile/hypothetical-growth-of-10k-compare?xids=${xid}&timeframe=${timeFrame}`
    });
  }

  return screenerApiGet({
    ...rest,
    apiType: PROFILE_API_TYPES.prop,
    path: `/profile/hypothetical-growth-of-10k/${xid}?timeframe=${timeFrame}`
  });
};
